import React from 'react';
import { Clock, GitPullRequestArrow, CheckCircle2, ClipboardList } from 'lucide-react';
import type { ProjectApprovalStats } from '../utils/groupApprovalsByProject';

interface Props {
  stats: ProjectApprovalStats;
}

interface Tile {
  label: string;
  value: number;
  hint: React.ReactNode;
  Icon: React.ComponentType<{ className?: string }>;
  hero?: boolean;
}

const AmsProjectApprovalSummaryCards: React.FC<Props> = ({ stats }) => {
  const tiles: Tile[] = [
    {
      label: 'Projects awaiting review',
      value: stats.awaitingReview,
      Icon: Clock,
      hero: true,
      hint: (
        <>
          <b>{stats.auditsAwaiting}</b> audits pending your review
        </>
      ),
    },
    {
      label: 'Projects with rejections',
      value: stats.withRejections,
      Icon: GitPullRequestArrow,
      hint: (
        <>
          <b>{stats.auditsRejected}</b> audits · waiting on requester
        </>
      ),
    },
    {
      label: 'Approved to start',
      value: stats.approvedToStart,
      Icon: CheckCircle2,
      hint: (
        <>
          <b>{stats.auditsApproved}</b> audits ready to begin
        </>
      ),
    },
    {
      label: 'Projects in review',
      value: stats.totalProjects,
      Icon: ClipboardList,
      hint: (
        <>
          <b>{stats.totalAudits}</b> submitted audits · drafts hidden
        </>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 font-[family-name:var(--font-outfit)]">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={`relative overflow-hidden rounded-2xl border p-4 shadow-sm ${
            t.hero ? 'border-amber-200 bg-gradient-to-b from-amber-50 to-white' : 'border-gray-200 bg-white'
          }`}
        >
          <div
            className={`absolute top-3 right-3 grid h-7 w-7 place-items-center rounded-lg border ${
              t.hero ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-gray-200 bg-gray-50 text-gray-500'
            }`}
          >
            <t.Icon className="h-4 w-4" />
          </div>
          <p
            className={`text-[10.5px] font-semibold uppercase tracking-wider ${
              t.hero ? 'text-amber-700' : 'text-gray-500'
            }`}
          >
            {t.label}
          </p>
          <p
            className={`mt-1 text-[28px] font-bold leading-none tabular-nums ${
              t.hero ? 'text-amber-700' : 'text-gray-900'
            }`}
          >
            {t.value} <span className="text-xs font-semibold text-gray-400">projects</span>
          </p>
          <p className="mt-1.5 text-[11.5px] text-gray-400">{t.hint}</p>
        </div>
      ))}
    </div>
  );
};

export default AmsProjectApprovalSummaryCards;
